import type { OpencodeToolPart } from "schemas/generated/opencode";

import React, { useCallback, useState } from "react";

import { useSessionActions } from "@agent-ui/features/chat/hooks/useOpencodeSession";
import { useSessionStoreActions } from "@agent-ui/features/chat/store/SessionStoreContext";
import { Check, Circle, HelpCircle, Loader2 } from "lucide-react";
import { motion } from "motion/react";
import { ReploError } from "schemas/errors";

import { QuestionFlow } from "./QuestionFlow";

class QuestionError extends ReploError {}

/**
 * NOTE (Yuxin, 2026-01-25): Agent question tool UI.
 * Displays questions with radio-button style clickable options.
 */

// NOTE (Yuxin, 2026-01-25): Cache answered questions by tool call ID
// This persists across component remounts caused by SSE stream updates
// For single questions: stores the selected label as a string
// For multi-question flows: stores a summary string of all answers
const answeredQuestions = new Map<string, string>();

interface QuestionOption {
  id: string;
  label: string;
  description?: string;
}

interface AgentQuestion {
  id: string;
  prompt?: string;
  question?: string;
  header?: string;
  options: QuestionOption[];
  allow_multiple?: boolean;
}

interface QuestionToolBlockProps {
  toolPart: OpencodeToolPart;
  sessionUuid: string;
}

export function QuestionToolBlock({
  toolPart,
  sessionUuid,
}: QuestionToolBlockProps) {
  const { getQuestionId } = useSessionStoreActions();
  const { answerQuestion, isAnsweringQuestion } = useSessionActions();

  // Check if this question was already answered (survives remounts)
  const cachedAnswer = answeredQuestions.get(toolPart.callID);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(
    cachedAnswer ?? null,
  );

  const input = toolPart.state.input as Record<string, unknown> | undefined;
  const questions = input?.questions as AgentQuestion[] | undefined;
  const firstQuestion = questions?.[0];
  const isMultiQuestion = questions && questions.length > 1;

  // NOTE (Yuxin, 2026-02-04): Handle multi-question flow completion
  const handleMultiQuestionComplete = useCallback(
    (answers: Record<string, string[]>) => {
      const questionId = getQuestionId({ toolCallId: toolPart.callID });
      if (!questionId) {
        throw new QuestionError({
          message: "No question ID found for tool call",
          additionalData: { toolCallId: toolPart.callID },
        });
      }

      // Store summary in cache only after confirming questionId exists,
      // so the user can retry if the question.asked event hasn't arrived yet
      const summary = Object.values(answers).flat().join(", ");
      answeredQuestions.set(toolPart.callID, summary);
      setSelectedAnswer(summary);

      // Convert Record<string, string[]> to string[][] in question order
      const answerArrays = (questions ?? []).map((question) => {
        return answers[question.id] ?? [];
      });

      void answerQuestion({
        sessionUuid,
        questionId,
        answers: answerArrays,
      });
    },
    [toolPart.callID, questions, sessionUuid, getQuestionId, answerQuestion],
  );

  const handleSelectOption = useCallback(
    (option: QuestionOption) => {
      if (selectedAnswer) {
        return;
      }

      const questionId = getQuestionId({ toolCallId: toolPart.callID });
      if (!questionId) {
        throw new QuestionError({
          message: "No question ID found for tool call",
          additionalData: { toolCallId: toolPart.callID },
        });
      }

      // Store in cache only after confirming questionId exists,
      // so the user can retry if the question.asked event hasn't arrived yet
      answeredQuestions.set(toolPart.callID, option.label);
      setSelectedAnswer(option.label);

      void answerQuestion({
        sessionUuid,
        questionId,
        answers: [[option.label]],
      });
    },
    [
      selectedAnswer,
      toolPart.callID,
      sessionUuid,
      getQuestionId,
      answerQuestion,
    ],
  );

  // If tool completed or we selected an answer, show completed state
  const isToolCompleted = toolPart.state.status === "completed";
  if (isToolCompleted || selectedAnswer) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Check size={14} />
        <span>Selected: {selectedAnswer ?? "answer"}</span>
      </div>
    );
  }

  // If no questions in input, show loading state
  if (!firstQuestion) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 size={14} className="animate-spin" />
        <span>Loading question...</span>
      </div>
    );
  }

  // NOTE (Yuxin, 2026-02-04): Multi-question flow - use QuestionFlow wizard
  if (isMultiQuestion && questions) {
    return (
      <QuestionFlow
        id={toolPart.callID}
        steps={questions.map((question) => ({
          id: question.id,
          title: question.prompt ?? question.question ?? "Question",
          description: question.header,
          options: question.options.map((opt) => ({
            id: opt.id,
            label: opt.label,
            description: opt.description,
          })),
          selectionMode: question.allow_multiple ? "multi" : "single",
        }))}
        onComplete={handleMultiQuestionComplete}
      />
    );
  }

  // Show single question with radio-style options
  return (
    <motion.div
      className="flex flex-col gap-3 w-full"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Question header and text */}
      <div className="flex flex-col gap-1">
        {firstQuestion.header && (
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {firstQuestion.header}
          </span>
        )}
        <div className="flex items-start gap-2">
          <HelpCircle size={16} className="mt-0.5 flex-shrink-0" />
          <span className="text-sm font-medium">
            {firstQuestion.prompt ?? firstQuestion.question}
          </span>
        </div>
      </div>

      {/* Radio-style options */}
      <div className="flex flex-col gap-0.5 ml-6">
        {firstQuestion.options.map((option, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handleSelectOption(option)}
            disabled={isAnsweringQuestion}
            className="group flex items-start gap-2.5 py-1.5 px-1 rounded text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {/* Radio circle - fills on hover */}
            <div className="flex-shrink-0 mt-0.5 relative">
              <Circle
                size={14}
                className="text-muted group-hover:text-primary transition-colors"
                strokeWidth={1.5}
              />
              {/* Fill circle on hover */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-2 h-2 rounded-full bg-primary" />
              </div>
            </div>

            {/* Option content */}
            <div className="flex flex-col min-w-0">
              <span className="text-sm text-default group-hover:text-primary transition-colors">
                {option.label}
              </span>
              {option.description && (
                <span className="text-xs text-muted-foreground">
                  {option.description}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </motion.div>
  );
}
